/**
 * Dialog — pattern 3 parties strict :
 *   <DialogHeader>  → header coloré (bg-primary, texte primary-foreground)
 *   <DialogBody>    → corps scrollable padded
 *   <DialogFooter>  → footer actions à droite
 *
 * Radius = 0 (flat design). Bouton fermeture positionné dans le header.
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  forwardRef,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementRef,
  type HTMLAttributes,
} from "react";

import { cn } from "@/lib/utils";

// ── Effet « genie » (macOS) ─────────────────────────────────────────────────
// La modale SURGIT du bouton qui l'a ouverte et S'Y REPLIE à la fermeture.
// On mémorise le point du dernier pointerdown (le déclencheur) ; à l'ouverture,
// la modale calcule son décalage par rapport au centre de l'écran et l'expose
// en variables CSS consommées par les keyframes dialog-genie-* (index.css).
let lastPointerDown = { x: 0, y: 0, t: 0 };
if (typeof window !== "undefined") {
  window.addEventListener(
    "pointerdown",
    (e) => {
      lastPointerDown = { x: e.clientX, y: e.clientY, t: Date.now() };
    },
    true,
  );
}

/** Décalage (bouton → centre) figé à l'ouverture : la fermeture revient au
 *  MÊME bouton, même si l'on ferme via ✕ ou Annuler. Ouverture programmée
 *  (sans clic récent) → simple zoom depuis le centre. */
function useGenieOrigin(): CSSProperties {
  const [origin] = useState(() => {
    if (typeof window === "undefined") return { dx: 0, dy: 0 };
    const fresh = Date.now() - lastPointerDown.t < 1500;
    if (!fresh) return { dx: 0, dy: 0 };
    return {
      dx: lastPointerDown.x - window.innerWidth / 2,
      dy: lastPointerDown.y - window.innerHeight / 2,
    };
  });
  return { "--genie-x": `${origin.dx}px`, "--genie-y": `${origin.dy}px` } as CSSProperties;
}

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, style, ...props }, ref) => {
  const genieStyle = useGenieOrigin();
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        // La modale ne se ferme PAS au clic extérieur ni avec Échap : uniquement
        // via le bouton Fermer/Annuler ou un enregistrement. (Surchargable par le
        // parent en repassant ces handlers.)
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        style={{ ...genieStyle, ...style }}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%]",
          "border border-border bg-surface shadow-2xl",
          // Mobile : plein écran sans rounded
          "max-h-[100dvh] h-[100dvh] sm:h-auto sm:max-h-[90vh]",
          // Mobile plein écran = carré ; desktop = rayon doux unifié (8px)
          "rounded-none sm:rounded",
          // Flex column pour structure 3 parties
          "flex flex-col overflow-hidden",
          // Effet genie : surgit du bouton déclencheur, s'y replie (index.css).
          "dialog-genie",
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * DialogHeader — partie 1/3 : bandeau coloré primary avec titre + description
 * + bouton fermer intégré à droite.
 */
export const DialogHeader = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "mada-dialog-header relative flex flex-col gap-1 bg-primary px-6 py-4 text-primary-foreground",
      className,
    )}
    {...props}
  >
    {children}
    <DialogPrimitive.Close
      className="mada-dialog-close absolute right-3 top-3 rounded p-1 opacity-80 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-foreground"
      aria-label="Fermer"
    >
      <X className="h-4 w-4" />
    </DialogPrimitive.Close>
  </div>
);

/**
 * DialogBody — partie 2/3 : zone scrollable interne du formulaire.
 */
export const DialogBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex-1 overflow-y-auto px-6 py-5",
      className,
    )}
    {...props}
  />
);

/**
 * DialogFooter — partie 3/3 : barre d'actions alignée à droite.
 */
export const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 border-t border-border bg-background/40 px-6 py-3 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-base font-semibold leading-tight text-primary-foreground", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-xs text-primary-foreground/80 leading-snug", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
